/**
 * InfoContent - Restaurant Information Tab
 * 
 * Clean card-free design:
 * - Sections with subtle dividers
 * - Premium typography
 * - Action buttons inline
 */

import React from 'react';
import { Phone, MapPin, Globe, Clock, Truck } from 'lucide-react';

interface PhoneNumber {
  number: string;
  type?: string;
}

// Accept all possible field name variants from API
interface OpeningHourRaw {
  // Day variants
  dayOfWeek?: number;
  weekday?: number;
  day?: number;
  // Time variants
  openTime?: string;
  closeTime?: string;
  openStart?: string;
  openEnd?: string;
  opening?: string;
  closing?: string;
  // Other
  isClosed?: boolean;
}

interface NormalizedHour {
  dayOfWeek: number;
  openTime: string | null;
  closeTime: string | null;
  isClosed: boolean;
}

interface Category {
  id: number | string;
  name: string;
}

interface Skill {
  id: number | string;
  name: string;
}

interface InfoContentProps {
  phoneNumbers: PhoneNumber[];
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  restaurantName: string;
  website?: string | null;
  openingHours: OpeningHourRaw[];
  deliveryHours?: OpeningHourRaw[] | null;
  hasDelivery: boolean;
  skills: Skill[];
  description?: string | null;
  categories: Category[];
  onCall: () => void;
  onRoute: () => void;
  onWebsite: () => void;
}

const dayNames = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const dayNamesIso = ['', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

// Get day name handling both JS (0=Sunday) and ISO (7=Sunday) formats
function getDayName(dayOfWeek: number): string {
  if (dayOfWeek === 7) return 'Sonntag';
  if (dayOfWeek >= 0 && dayOfWeek <= 6) return dayNames[dayOfWeek];
  if (dayOfWeek >= 1 && dayOfWeek <= 7) return dayNamesIso[dayOfWeek];
  return `Tag ${dayOfWeek}`;
}

// Group opening hours by day, combining multiple time slots
interface GroupedHour {
  dayOfWeek: number;
  times: Array<{ openTime: string | null; closeTime: string | null }>;
  isClosed: boolean;
}

// Normalize opening hours from any API format
function normalizeOpeningHours(hours: OpeningHourRaw[]): NormalizedHour[] {
  return hours
    .map(h => {
      // Get day from various possible field names
      const day = h.dayOfWeek ?? h.weekday ?? h.day;
      if (day === undefined || day === null) return null;
      
      // Get times from various possible field names
      const openTime = h.openTime || h.openStart || h.opening || null;
      const closeTime = h.closeTime || h.openEnd || h.closing || null;
      
      return {
        dayOfWeek: day,
        openTime,
        closeTime,
        isClosed: h.isClosed ?? false,
      };
    })
    .filter((h): h is NormalizedHour => h !== null);
}

export const InfoContent: React.FC<InfoContentProps> = ({
  phoneNumbers,
  street,
  houseNumber,
  postalCode,
  city,
  website,
  openingHours,
  hasDelivery,
  skills,
  description,
  categories,
  onCall,
  onRoute,
  onWebsite,
}) => {
  const address = `${street} ${houseNumber}, ${postalCode} ${city}`;
  const primaryPhone = phoneNumbers[0]?.number;
  const today = new Date().getDay();

  // Normalize and sort opening hours by day (Monday first, Sunday last)
  const normalizedHours = normalizeOpeningHours(openingHours);
  
  // Group hours by day to handle multiple time slots per day
  const hoursByDay = new Map<number, GroupedHour>();
  
  normalizedHours.forEach(hour => {
    // Normalize day: convert ISO 7 to JS 0 for Sunday
    const normalizedDay = hour.dayOfWeek === 7 ? 0 : hour.dayOfWeek;
    
    if (!hoursByDay.has(normalizedDay)) {
      hoursByDay.set(normalizedDay, {
        dayOfWeek: normalizedDay,
        times: [],
        isClosed: hour.isClosed
      });
    }
    
    const group = hoursByDay.get(normalizedDay)!;
    if (!hour.isClosed && hour.openTime && hour.closeTime) {
      group.times.push({ openTime: hour.openTime, closeTime: hour.closeTime });
      group.isClosed = false;
    }
  });
  
  // Convert to array and sort (Monday=1 first, Sunday=0 last)
  const sortedHours = Array.from(hoursByDay.values()).sort((a, b) => {
    const aSort = a.dayOfWeek === 0 ? 7 : a.dayOfWeek;
    const bSort = b.dayOfWeek === 0 ? 7 : b.dayOfWeek;
    return aSort - bSort;
  });

  return (
    <div className="px-4 py-5 space-y-5">
      {/* Description */}
      {description && (
        <section 
          className="p-5 rounded-2xl"
          style={{ 
            background: 'rgba(255,255,255,0.9)',
            border: '1px solid rgba(0,0,0,0.04)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          }}
        >
          <p className="text-gray-700 text-sm leading-relaxed">{description}</p>
        </section>
      )}
      
      {/* Contact & Address */}
      <section className="space-y-2.5">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1">
          Kontakt & Adresse
        </h3>
        
        <div 
          className="rounded-2xl overflow-hidden divide-y divide-gray-100/80"
          style={{ 
            background: 'rgba(255,255,255,0.9)',
            border: '1px solid rgba(0,0,0,0.04)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          }}
        >
          {/* Phone */}
          {primaryPhone && (
            <button
              onClick={onCall}
              className="flex items-center gap-3.5 w-full py-3.5 px-4 transition-colors hover:bg-gray-50/60 active:scale-[0.99]"
            >
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, rgba(14,127,79,0.12), rgba(14,127,79,0.06))' }}
              >
                <Phone className="h-5 w-5 text-emerald-700" strokeWidth={1.8} />
              </div>
              <div className="flex-1 text-left">
                <span className="text-gray-900 font-medium text-sm">{primaryPhone}</span>
              </div>
              <span className="text-emerald-600 text-sm font-semibold">Anrufen</span>
            </button>
          )}
          
          {/* Address */}
          <button
            onClick={onRoute}
            className="flex items-center gap-3.5 w-full py-3.5 px-4 transition-colors hover:bg-gray-50/60 active:scale-[0.99]"
          >
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, rgba(37,99,235,0.12), rgba(37,99,235,0.06))' }}
            >
              <MapPin className="h-5 w-5 text-blue-600" strokeWidth={1.8} />
            </div>
            <div className="flex-1 text-left">
              <span className="text-gray-900 font-medium text-sm">{address}</span>
            </div>
            <span className="text-blue-600 text-sm font-semibold">Route</span>
          </button>
          
          {/* Website */}
          {website && (
            <button
              onClick={onWebsite}
              className="flex items-center gap-3.5 w-full py-3.5 px-4 transition-colors hover:bg-gray-50/60 active:scale-[0.99]"
            >
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, rgba(107,114,128,0.12), rgba(107,114,128,0.06))' }}
              >
                <Globe className="h-5 w-5 text-gray-600" strokeWidth={1.8} />
              </div>
              <div className="flex-1 text-left truncate">
                <span className="text-gray-900 font-medium text-sm truncate">
                  {website.replace(/^https?:\/\//, '')}
                </span>
              </div>
              <span className="text-gray-500 text-sm font-semibold">Öffnen</span>
            </button>
          )}
        </div>
      </section>
      
      {/* Opening Hours - Only show if hours exist */}
      {sortedHours.length > 0 && (
        <section className="space-y-2.5">
          <div className="flex items-center gap-2 px-1">
            <Clock className="h-4 w-4 text-gray-400" strokeWidth={1.8} />
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Öffnungszeiten
            </h3>
          </div>
          
          <div 
            className="rounded-2xl overflow-hidden"
            style={{ 
              background: 'rgba(255,255,255,0.9)',
              border: '1px solid rgba(0,0,0,0.04)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
            }}
          >
            {sortedHours.map((hour, index) => {
              const isToday = hour.dayOfWeek === today;
              const dayName = getDayName(hour.dayOfWeek);
              
              // Format times - combine multiple slots with comma
              const timeDisplay = hour.isClosed 
                ? 'Geschlossen' 
                : hour.times.length > 0 
                  ? hour.times.map(t => `${t.openTime} – ${t.closeTime}`).join(', ')
                  : 'Keine Angabe';
              
              return (
                <div
                  key={hour.dayOfWeek}
                  className={`flex justify-between items-center py-3 px-4 ${
                    index !== sortedHours.length - 1 ? 'border-b border-gray-100' : ''
                  }`}
                  style={isToday ? { background: 'rgba(14,127,79,0.06)' } : undefined}
                >
                  <span className={`text-sm ${isToday ? 'font-semibold text-emerald-700' : 'text-gray-700'}`}>
                    {dayName}
                    {isToday && <span className="ml-1.5 text-xs">(heute)</span>}
                  </span>
                  <span className={`text-sm text-right ${hour.isClosed ? 'text-gray-400' : isToday ? 'font-medium text-emerald-700' : 'text-gray-600'}`}>
                    {timeDisplay}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}
      
      {/* Categories & Skills */}
      {(categories.length > 0 || skills.length > 0) && (
        <section className="space-y-2.5">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1">
            Kategorien & Angebot
          </h3>
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <span
                key={cat.id}
                className="px-3.5 py-1.5 rounded-full text-sm font-medium text-emerald-800"
                style={{ 
                  background: 'linear-gradient(135deg, rgba(14,127,79,0.1), rgba(14,127,79,0.05))',
                  border: '1px solid rgba(14,127,79,0.1)',
                }}
              >
                {cat.name}
              </span>
            ))}
            {skills.map(skill => (
              <span
                key={skill.id}
                className="px-3.5 py-1.5 rounded-full text-sm font-medium text-gray-700"
                style={{ 
                  background: 'rgba(107,114,128,0.06)',
                  border: '1px solid rgba(107,114,128,0.08)',
                }}
              >
                {skill.name}
              </span>
            ))}
          </div>
        </section>
      )}
      
      {/* Delivery Info */}
      {hasDelivery && (
        <section 
          className="flex items-center gap-3 p-4 rounded-2xl"
          style={{ 
            background: 'linear-gradient(135deg, rgba(37,99,235,0.06), rgba(37,99,235,0.02))',
            border: '1px solid rgba(37,99,235,0.08)',
          }}
        >
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(37,99,235,0.1)' }}
          >
            <Truck className="h-5 w-5 text-blue-600" strokeWidth={1.8} />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">Lieferung verfügbar</p>
            <p className="text-xs text-gray-500 mt-0.5">Dieses Restaurant bietet einen Lieferservice an.</p>
          </div>
        </section>
      )}
    </div>
  );
};

export default InfoContent;
